import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

const SetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      setError('No setup token provided');
      return;
    }
    
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await API.get(`/auth/verify-setup-token?token=${token}`);
      setTokenValid(true);
      setUserInfo(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired setup link');
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      await API.post('/auth/set-password', {
        token,
        password
      });
      setSuccess(true);
      toast.success('Password set successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set password');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center p-6">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#2C5530] mx-auto mb-4" />
          <p className="text-[#57534E]">Verifying setup link...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center p-6">
        <div className="bg-white border border-[#E7E5E4] p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-2">Invalid Link</h1>
          <p className="text-[#57534E] mb-6">{error}</p>
          <p className="text-sm text-[#737373] mb-6">
            If you need a new setup link, please contact your administrator.
          </p>
          <Link
            to="/login"
            className="inline-block bg-[#2C5530] text-white px-6 py-3 font-semibold hover:bg-[#1E3A21] transition-colors"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    const isCoach = userInfo?.role === 'coach';
    const loginPath = isCoach ? '/coach-login' : '/login';
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center p-6">
        <div className="bg-white border border-[#E7E5E4] p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-[#2C5530] mx-auto mb-4" />
          <h1 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-2">Account Activated!</h1>
          <p className="text-[#57534E] mb-6">
            Your password has been set and your {isCoach ? 'coach' : ''} account is now active.
          </p>
          <button
            onClick={() => navigate(loginPath)}
            className="w-full bg-[#2C5530] text-white px-6 py-3 font-semibold hover:bg-[#1E3A21] transition-colors"
            data-testid="go-to-login-btn"
          >
            Sign In to Your {isCoach ? 'Coach' : ''} Account
          </button>
        </div>
      </div>
    );
  }

  // Set password form
  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center p-6">
      <div className="bg-white border border-[#E7E5E4] p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_golf-experience-hub/artifacts/amevwgso_logo-for-web%20copy.jpg" 
              alt="Quiet Mind Golf" 
              className="h-12 w-auto"
            />
            <span className="font-['Playfair_Display'] text-xl font-medium text-[#1A1A1A]">
              Quiet Mind Golf
            </span>
          </Link>
          <h1 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-2">
            Set Your Password
          </h1>
          {userInfo && (
            <p className="text-[#57534E]">
              Welcome, <span className="font-semibold">{userInfo.full_name}</span>!<br />
              Create a password to activate your coach account.
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="set-password-form">
          {/* Email (read-only) */}
          <div>
            <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
              Email
            </label>
            <input
              type="email"
              value={userInfo?.email || ''}
              disabled
              className="w-full px-4 py-3 border border-[#E7E5E4] bg-[#F2F0E9] text-[#737373] cursor-not-allowed"
            />
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-[#1A1A1A] mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#737373]" />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border border-[#E7E5E4] bg-white text-[#1A1A1A] placeholder:text-[#737373] focus:border-[#2C5530] focus:outline-none"
                placeholder="Create a password"
                required
                minLength={6}
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] hover:text-[#1A1A1A]"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-xs text-[#737373] mt-1">Minimum 6 characters</p>
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-[#1A1A1A] mb-2">
              Confirm Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#737373]" />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border border-[#E7E5E4] bg-white text-[#1A1A1A] placeholder:text-[#737373] focus:border-[#2C5530] focus:outline-none"
                placeholder="Confirm your password"
                required
                data-testid="confirm-password-input"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] hover:text-[#1A1A1A]"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#2C5530] text-white py-3 font-semibold uppercase tracking-wider hover:bg-[#1E3A21] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="submit-password-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Setting Password...
              </>
            ) : (
              'Activate Account'
            )}
          </button>
        </form>

        <p className="text-center text-sm text-[#737373] mt-6">
          Already have an account?{' '}
          <Link to={userInfo?.role === 'coach' ? '/coach-login' : '/login'} className="text-[#2C5530] font-semibold hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default SetPasswordPage;
